import { useEffect, useState } from 'react';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { useTiers } from '../hooks/useTiers';
import LoadingSpinner from '../components/LoadingSpinner';
import UserDetailModal from '../components/UserDetailModal';
import ConfirmationModal from '../components/ConfirmationModal';
import TierChangeModal from '../components/TierChangeModal';
import { getTierName } from '../utils/tier';

export default function UserListPage() {
  /* c8 ignore start */
  const getDisplayName = (u) => u?.displayName || u?.display_name || u?.twitch_username || 'unknown';
  /* c8 ignore stop */
  const getAvatarSrc = (u) => {
    // Prefer canonical avatar fields returned from backend; fall back to a generated avatar
    const src = u?.profile_image_url || u?.avatar_url || u?.thumbnail_url;
    if (src) return src;
    try {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(getDisplayName(u))}&background=222222&color=ffffff&rounded=true&size=64`;
    /* c8 ignore start */
    } catch (e) {
      return '';
    }
    /* c8 ignore stop */
  };
  const { 
    users, 
    loading, 
    error, 
    pagination, 
    fetchUsers, 
    fetchUser, 
    updateUserStatus,
    updateUserAdmin,
    updateUserTier,
    deleteUser,
  } = useAdminUsers();
  const { tiers, fetchTiers } = useTiers();
  
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [detailModal, setDetailModal] = useState({ isOpen: false, userId: null });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, user: null, action: null });
  const [tierChangeModal, setTierChangeModal] = useState({ isOpen: false, user: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Fetch tiers for filter dropdown
  useEffect(() => {
    fetchTiers({ limit: 100, status: 'active' });
  }, [fetchTiers]);

  // Fetch users when filters/sort change
  useEffect(() => {
    const options = {
      page: currentPage,
      limit: 50,
      order_by: sortField,
      order: sortOrder,
    };

    if (searchQuery) options.search = searchQuery;
    if (statusFilter) options.status = statusFilter;
    if (adminFilter !== '') options.is_admin = adminFilter === 'true';
    if (tierFilter) options.tier_id = tierFilter;

    fetchUsers(options);
  }, [fetchUsers, currentPage, sortField, sortOrder, searchQuery, statusFilter, adminFilter, tierFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
    /* c8 ignore start */
    setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    /* c8 ignore stop */
    } else {
      setSortField(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortOrder === 'ASC') {
      return (
        <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') setStatusFilter(value);
    if (filterType === 'admin') setAdminFilter(value);
    if (filterType === 'tier') setTierFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const formatDate = (dateString) => {
    /* c8 ignore start */
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    /* c8 ignore stop */
  };

  // Note: use the exported `getTierName(tiers, id)` helper above

  // Action handlers
  const handleStatusToggle = (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    setActionError(null);
    setConfirmModal({
      isOpen: true,
      user,
      action: 'status',
      newStatus,
    });
  };

  const handleAdminToggle = (user) => {
    const newAdminStatus = !user.is_admin;
    setActionError(null);
    setConfirmModal({
      isOpen: true,
      user,
      action: 'admin',
      newAdminStatus,
    });
  };

  const handleTierChange = (user) => {
    setActionError(null);
    setTierChangeModal({ isOpen: true, user });
  };

  const handleDelete = (user) => {
    setActionError(null);
    setConfirmModal({
      isOpen: true,
      user,
      action: 'delete',
    });
  };

  const handleConfirmAction = async () => {
    /* c8 ignore start */
    if (!confirmModal.user) return;
    /* c8 ignore stop */

    setIsProcessing(true);
    setActionError(null);

    try {
      if (confirmModal.action === 'status') {
        await updateUserStatus(confirmModal.user.id, confirmModal.newStatus);
      } else if (confirmModal.action === 'admin') {
        await updateUserAdmin(confirmModal.user.id, confirmModal.newAdminStatus);
      /* c8 ignore start */
      } else if (confirmModal.action === 'delete') {
        await deleteUser(confirmModal.user.id);
        setDetailModal({ isOpen: false, userId: null }); // Close detail modal after delete
      }
      /* c8 ignore stop */

      setConfirmModal({ isOpen: false, user: null, action: null });
      
      // Refresh user list
      await fetchUsers({
        page: currentPage,
        limit: 50,
        order_by: sortField,
        order: sortOrder,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        /* c8 ignore start */
        is_admin: adminFilter !== '' ? adminFilter === 'true' : undefined,
        /* c8 ignore stop */
        tier_id: tierFilter || undefined,
      });
    } catch (err) {
      setActionError(err.message);
      console.error('Action failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmTierChange = async (userId, tierId) => {
    setIsProcessing(true);
    setActionError(null);

    try {
      await updateUserTier(userId, tierId);
      setTierChangeModal({ isOpen: false, user: null });
      
      // Refresh user list
      await fetchUsers({
        page: currentPage,
        limit: 50,
        order_by: sortField,
        order: sortOrder,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        /* c8 ignore start */
        is_admin: adminFilter !== '' ? adminFilter === 'true' : undefined,
        /* c8 ignore stop */
        tier_id: tierFilter || undefined,
      });
    } catch (err) {
      setActionError(err.message);
      console.error('Tier change failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseConfirmModal = () => {
    if (!isProcessing) {
      setConfirmModal({ isOpen: false, user: null, action: null });
      setActionError(null);
    }
  };

  const handleCloseTierChangeModal = () => {
    if (!isProcessing) {
      setTierChangeModal({ isOpen: false, user: null });
      setActionError(null);
    }
  };

  if (loading && users.length === 0) {
    return <LoadingSpinner message="Loading users..." />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        <p className="font-medium">Error loading users</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <p className="text-gray-400 mt-1">Manage users, subscriptions, and permissions</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-400 mb-1">
              Search
            </label>
            <input
              id="search"
              type="text"
              placeholder="Username or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white placeholder-gray-500 focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-400 mb-1">
              Status
            </label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          {/* Admin Filter */}
          <div>
            <label htmlFor="admin-filter" className="block text-sm font-medium text-gray-400 mb-1">
              Admin Status
            </label>
            <select
              id="admin-filter"
              value={adminFilter}
              onChange={(e) => handleFilterChange('admin', e.target.value)}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All users</option>
              <option value="true">Admins only</option>
              <option value="false">Non-admins only</option>
            </select>
          </div>

          {/* Tier Filter */}
          <div>
            <label htmlFor="tier-filter" className="block text-sm font-medium text-gray-400 mb-1">
              Subscription Tier
            </label>
            <select
              id="tier-filter"
              value={tierFilter}
              onChange={(e) => handleFilterChange('tier', e.target.value)}
              className="w-full px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All tiers</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {users.length === 0 ? (
        <div className="text-center py-12 bg-dark-800 border border-dark-700 rounded-lg">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-700 mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No users found</h3>
          <p className="text-gray-400">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <>
          <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
            <table className="w-full" aria-label="Users list">
              <caption className="sr-only">
                List of users with email, Twitch ID, subscription tier, and status. Use filters above to narrow results.
              </caption>
              <thead className="bg-dark-700 border-b border-dark-600">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('twitch_username')}
                      className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                    >
                      Username
                      {getSortIcon('twitch_username')}
                    </button>
                  </th>
                  {/* Email column removed */}
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('subscription_tier_id')}
                      className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                    >
                      Subscription
                      {getSortIcon('subscription_tier_id')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-center" title="Total clips in collection">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Clips Added
                    </span>
                  </th>
                  <th className="px-6 py-3 text-center" title="Clips tagged with labels">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Tagged
                    </span>
                  </th>
                  <th className="px-6 py-3 text-center" title="Unique tags used">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Unique Tags
                    </span>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('last_activity_at')}
                      className="flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wider hover:text-white transition-colors"
                    >
                      Activity
                      {getSortIcon('last_activity_at')}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-right">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {(() => {
                  /* c8 ignore start */
                  const uniqueUsers = Array.isArray(users)
                    ? Array.from(new Map(users.filter(Boolean).map((u) => [u.id, u])).values())
                    : [];
                  /* c8 ignore stop */
                  return uniqueUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className="hover:bg-dark-700 transition-colors cursor-pointer"
                    onClick={() => setDetailModal({ isOpen: true, userId: user.id })}
                  >
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <img
                          src={getAvatarSrc(user)}
                          alt={`${getDisplayName(user)}'s avatar`}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{getDisplayName(user)}</span>
                          {user?.is_admin && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium bg-purple-900/30 text-purple-400 border border-purple-500/30">
                              {/* c8 ignore start */}
                              {user.isRootAdmin ? 'Root Admin' : 'Admin'}
                              {/* c8 ignore stop */}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* Email column removed */}
                    <td className="px-6 py-2 whitespace-nowrap align-middle">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-300 font-medium">{getTierName(tiers, user.subscription_tier_id)}</span>
                        {/* c8 ignore start */}
                        <span
                          className={`inline-flex items-center px-1 py-0 rounded text-xs font-medium leading-tight w-fit ${
                            user.status === 'active'
                              ? 'bg-green-900/40 text-green-300'
                              : (user.status === 'disabled' || user.status === 'suspended')
                              ? 'bg-yellow-900/40 text-yellow-300'
                              : 'bg-red-900/40 text-red-300'
                          }`}
                          title={`Expires: ${user.subscription_expires_at ? new Date(user.subscription_expires_at).toISOString().split('T')[0] : '-'}`}
                        >
                          {user.status}
                        </span>
                        {/* c8 ignore stop */}
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-center">
                      <span 
                        className="text-xs text-gray-400 cursor-help"
                        title={`${user.creator_clip_count || 0} created + ${(user.collection_clip_count || 0) - (user.creator_clip_count || 0)} added`}
                      >
                        {user.collection_clip_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-center">
                      <span className="text-xs text-gray-400">{user.tagged_clip_count || 0}</span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-center">
                      <span className="text-xs text-gray-400">{user.unique_tag_count || 0}</span>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap align-middle">
                      <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                        <span>Joined: {new Date(user.created_at).toISOString().split('T')[0]}</span>
                        <span>Updated: {user.updated_at ? new Date(user.updated_at).toISOString().split('T')[0] : '-'}</span>
                        <span>Activity: {user.last_activity_at ? new Date(user.last_activity_at).toISOString().split('T')[0] : '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-right">
                      <button
                        onClick={() => setDetailModal({ isOpen: true, userId: user.id })}
                        className="px-3 py-1 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
                        aria-label={`View ${getDisplayName(user)}`}
                        title="View user"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
                  })()}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-white bg-dark-700 border border-dark-600 rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-2 text-sm font-medium text-white bg-dark-700 border border-dark-600 rounded-lg hover:bg-dark-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* User Detail Modal */}
      <UserDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, userId: null })}
        userId={detailModal.userId}
        fetchUser={fetchUser}
        onStatusToggle={handleStatusToggle}
        onAdminToggle={handleAdminToggle}
        onTierChange={handleTierChange}
        onDelete={handleDelete}
      />

      {/* Confirmation Modal for Status/Admin/Delete */}
      {confirmModal.action && (
        <ConfirmationModal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleConfirmAction}
          title={
            confirmModal.action === 'status'
              ? `${confirmModal.newStatus === 'active' ? 'Enable' : 'Disable'} User`
              : confirmModal.action === 'admin'
              ? `${confirmModal.newAdminStatus ? 'Promote to' : 'Demote from'} Admin`
              : 'Delete User'
          }
          message={
            confirmModal.action === 'status'
              ? `Are you sure you want to ${confirmModal.newStatus === 'active' ? 'enable' : 'disable'} ${getDisplayName(confirmModal.user)}?`
              : confirmModal.action === 'admin'
              ? `Are you sure you want to ${confirmModal.newAdminStatus ? 'promote' : 'demote'} ${getDisplayName(confirmModal.user)}?`
              : `Are you sure you want to permanently delete ${getDisplayName(confirmModal.user)}? This action cannot be undone.`
          }
          confirmText={
            confirmModal.action === 'status'
              ? confirmModal.newStatus === 'active' ? 'Enable' : 'Disable'
              : confirmModal.action === 'admin'
              ? confirmModal.newAdminStatus ? 'Promote' : 'Demote'
              : 'Delete'
          }
          variant={confirmModal.action === 'delete' ? 'danger' : 'primary'}
          isProcessing={isProcessing}
        />
      )}

      {/* Tier Change Modal */}
      <TierChangeModal
        isOpen={tierChangeModal.isOpen}
        onClose={handleCloseTierChangeModal}
        onConfirm={handleConfirmTierChange}
        user={tierChangeModal.user}
        availableTiers={tiers}
        isProcessing={isProcessing}
        errorMessage={actionError}
      />
    </div>
  );
}
